/* global google, mapsInitialized, iconGenerator */
import { MarkerClusterer } from '@googlemaps/markerclusterer';

customElements.define('find-store-automplete-results', class FindStoreAutompleteResults extends HTMLElement {
    static autocompleteResultsTemplate(item) {
        return `<li class="find-store-automplete-results__item"><div tabindex="0"
        class="find-store-automplete-results__button" data-suggestion-index="${item.index}">${item.description}</div></li>`;
        // safari on mac does not support click/focus events on buttons, so therefore it's now a DIV instead
    }

    constructor() {
        super();
        this.elements={};
        this.elements.resultsElem = this.querySelector('#inventory-search-form-autocomplete-results');
    }

    /**
    * Renders the locations found in autocomplete dropdown
    */
    _render(results) {
        let resultsMapped = results.map((x,index) => ({
            ...x,
            index:index
        }));

        this.elements.resultsElem.innerHTML = resultsMapped
        .map(this.constructor.autocompleteResultsTemplate)
        .join('');
    }
});

class FindInStore extends HTMLElement {
    constructor() {
        super();
        this.testMode = false; // when testMode is true, "Find in store overlay" will open on load and open opening hours of first store
        this.elements={};

        this.elements.detailsElem = this.querySelector('.find-in-store');
        if(this.elements.detailsElem) {
            this.initOpenOverlay();
        }

        //close on click on overlay background
        this.elements.stopBubblingElement = this.querySelector('.page-width');
        if(this.elements.stopBubblingElement) {
            this.elements.stopBubblingElement.addEventListener('click', (e) => {
                e.stopPropagation();
            })
        }

        this.elements.overlayBackground = this.querySelector('.fullwidth-overlay__inner');
        if(this.elements.overlayBackground) {
            this.elements.overlayBackground.addEventListener('click', () => {
                this.closeOverlay();
            })
        }

        //closebutton
        this.elements.visibleCloseButton = this.querySelector('.fullwidth-overlay__close--in-content');
        if(this.elements.visibleCloseButton) {
            this.elements.visibleCloseButton.addEventListener('click', () => {
                this.closeOverlay();
            })
        }

        if(this.testMode) {
            this.setupTestMode();
        }
    }

    /**
    * Used for opening Find in store overlay on load, when in testMode
    */
    setupTestMode() {
        this.elements.detailsElem.setAttribute('open','true');
        this.elements.storeInventoryResultsElem = this.querySelector('store-inventory-results');
        this.elements.storeInventoryResultsElem.testMode = true;
    }

    /**
    * add and remove certain class from body when overlay is shown/hidden
    * for locking scroll and styling close btn
    */
    initOpenOverlay() {
        this.elements.detailsElem.addEventListener('toggle', (e) => {
            if(e.target.hasAttribute('open')) {
                document.body.classList.add('find-in-store-overlay-open')
            } else {
                this.closeOverlay();
            }
        })
    }

    /**
    * for closing overlay
    */
    closeOverlay() {
        this.elements.detailsElem.removeAttribute('open');
        document.body.classList.remove('find-in-store-overlay-open');
    }
}
customElements.define('find-in-store', FindInStore);

class InventorySearchForm extends HTMLElement {
    constructor() {
        super();
        this._mapsApiKey = this.getAttribute('data-maps-api-key');
        this._autocompleteService;
        this.isStoreLocator = this.tagName.toLowerCase() === 'inventory-search-form-locator';

        this.elements={};
        this.elements.autoCompleteResults = this.querySelector('find-store-automplete-results');
        this.elements.inventorySearchSubmit = this.querySelector('#inventory-search-submit');
        this.elements.inventorySearchReset = this.querySelector('#inventory-search-reset');
        this.elementsOutside={};
        this.timeoutRef = null;
        this.suggestions = null;
        this.firstResult = null;
        this.currentUserLocation = null;
        this.detailViewData = {location: null};
        this.currentMapCenterLocation = null;
        this.sortedLocationsWithDistanceToUserSearch = [];
        this.markerLocations = [];
        this.selectedDistance = 500;
        this.clusterMarkers;
        this.updateAfterFetchingNewLocations;
        this.locationsToShow = [];
        this.markers = {};
        this.map = null;
        this.mapConfig = {
            Markers:{
                MarkerSize: 35,
                MarkerClickAction: 'showInfoWindow'
            },
            icons: [
                {normal:{}},
                {active:{}}
            ]
        }// not in use for find in store / tbb
        if(!this.isStoreLocator) {
            this.init();
        }
        this.options = {
            types: ['address'],
            componentRestrictions: {country: ['dk','se'] }//TODO: country must be set dynamically somewhere
        };
    }

    init() {
        if(this.isStoreLocator) {
            this.$storeInventoryResults = document.querySelector('store-inventory-results-locator');
        }else{
            this.$storeInventoryResults = document.querySelector('store-inventory-results');
        }

        this.dealers = this.$storeInventoryResults.allStores;

        this.elements.onlyInStockCheckbox = this.querySelector('#only-in-stock');
        if(this.elements.onlyInStockCheckbox) {
            this.initShowOnlyInStock();
        }

        this.elements.searchInput = this.querySelector('#inventory-search-field');
        if(this.elements.searchInput) {
            this.initSearchInput();
        }

        this.elements.searchAutoCompleteItems = this.querySelector('#inventory-search-form-autocomplete-results');
        if(this.elements.searchAutoCompleteItems) {
            this.initAutoCompleteItems();
        }

        if(!this._mapsApiKey) {return;}
        this.addMapsScript();
        if(mapsInitialized) {
            this.initMapsFromFile();
        } else {
            this.addEventListener('initGoogleMaps', () => {
                this.initMapsFromFile();
            });
        }

        this.elements.inventorySearchSubmit.addEventListener('click', () => {
            this.startSearchWithFirstSuggestion();
        });

        this.elements.inventorySearchReset.addEventListener('click', () => {
            this.resetSearch();
        });

        const stopBubblingSelector = this.isStoreLocator ? 'body' : '.fullwidth-overlay__inner--find-in-store > .page-width';
        this.elementsOutside.stopBubblingElement=document.querySelector(stopBubblingSelector);
        if(this.elementsOutside.stopBubblingElement) {
            this.elementsOutside.stopBubblingElement.addEventListener('click', e => {this.selectLocationEvent(e);})
            this.elementsOutside.stopBubblingElement.addEventListener('keyup', e => {this.selectLocationEvent(e);})
        }
    }

    setMarkerIcons() {
        const icons = [
            {
                name: 'normal',
                pColor: this.getAttribute('data-marker-normal-text') ? this.getAttribute('data-marker-normal-text') : 'black',
                pinColor: this.getAttribute('data-marker-normal-background') ? this.getAttribute('data-marker-normal-background') : 'white',
                borderColor: this.getAttribute('data-marker-normal-border') ? this.getAttribute('data-marker-normal-border') : 'grey'
            },
            {
                name: 'active',
                pColor: this.getAttribute('data-marker-active-text') ? this.getAttribute('data-marker-active-text') : 'white',
                pinColor: this.getAttribute('data-marker-active-background') ? this.getAttribute('data-marker-active-background') : 'black',
                borderColor: this.getAttribute('data-marker-active-border') ? this.getAttribute('data-marker-active-border') : 'grey'
            }
        ];

        for (let i = 0; i < icons.length; i++) {
            const icon = icons[i];
            let obj = {}
            const iconHtml =`<svg version="1.1" width="26" height="37" id="Layer_2" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
            viewBox="0 0 26 37" style="enable-background:new 0 0 26 37;" xml:space="preserve">

            <path fill="${icon.pinColor}" d="M13,34.5c-1.3-5-4.4-8.4-7.1-11.5C3,19.7,0.5,16.8,0.5,12.7c0-8,4.3-12.2,12.5-12.2s12.5,4.2,12.5,12.2
            c0,4.1-2.5,7-5.4,10.3C17.4,26.1,14.3,29.5,13,34.5z"/>
            <path fill="${icon.borderColor}" d="M13,1c8,0,12,3.9,12,11.7c0,3.9-2.5,6.7-5.3,10c-2.5,2.8-5.2,5.9-6.7,10.2c-1.5-4.3-4.2-7.4-6.7-10.2
            C3.5,19.4,1,16.6,1,12.7C1,4.9,5,1,13,1 M13,0C4.4,0,0,4.5,0,12.7C0,21.5,11.3,25,13,37c1.7-12,13-15.5,13-24.3C26,4.5,21.6,0,13,0
            L13,0z"/>
            <path fill="${icon.pColor}" d="M14.7,5.7H8.9v13.9h2.5v-4.8h3.3c2.6,0,4.6-1.7,4.6-4.6C19.3,7.4,17.3,5.7,14.7,5.7z M14.3,12.6h-2.9V7.9h2.9
            c1.3,0,2.3,1,2.3,2.2C16.6,12,15.2,12.6,14.3,12.6z"/>
            </svg>`;

            obj.url = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(iconHtml);
            const scaleX = 26;
            const scaleY = 37;
            obj.scaledSize=new google.maps.Size(scaleX,scaleY)
            this.mapConfig.icons[icon.name]=obj;
        }
    }

    resetMap(){
        if(this.isStoreLocator) {
            this.map.setZoom(this.mapStartSettings.zoom);
            this.map.panTo(this.mapStartSettings.center);
        }
    }

    selectLocationEvent(e) {
        if(e.target.classList.contains('find-store-automplete-results__button')) {
            const clickedIndex = parseInt(e.target.dataset.suggestionIndex,10);
            if(e.type.toLowerCase() !== 'keyup' || (e.type.toLowerCase() === 'keyup' && e.keyCode === 13)) {
                this.selectLocation(this.suggestions[clickedIndex]);
                this.resetMap();
            }
        }
    }

    /**
    * Catching event from checking and unchecking checkbox for
    * hiding products that are out of stock
    */
    initShowOnlyInStock() {
        this.elements.onlyInStockCheckbox.addEventListener('click', (e) => {
            const onlyInStockCheckbox = e.target;
            if(onlyInStockCheckbox.checked === true) {
                this.$storeInventoryResults.hideOutOfStock = true;
            } else {
                this.$storeInventoryResults.hideOutOfStock = false;
            }
            this.$storeInventoryResults._render();
        })
    }

    /**
    * Reset search field and search result list
    */
    resetSearch() {
        this.elements.searchInput.value='';
        this.$storeInventoryResults.results =[];
        this.$storeInventoryResults._render();
        this.elements.searchInput.focus();
    }

    /**
    * Fills the search field with the description of the passed location,
    * hides the suggestion box and starts the search for client specific
    * locations (clinics, shops,...) nearby the user selected location.
    *
    * @param {object} location - location object returned from the google places autocomplete
    */
    selectLocation(location) {
        if (location) {
            this.elements.searchInput.value = location.description;
            this.suggestions = [];
            this.elements.autoCompleteResults._render(this.suggestions);

            this.findLocationsInRadius(location);
        }
    }

    /**
    * Start a search using the first search result in the autocomplete dropdown
    */
    startSearchWithFirstSuggestion() {
        if (this.firstResult && document.getElementById('inventory-search-form-autocomplete-results').childElementCount > 0) {
            this.selectLocation(this.firstResult);
        }
    }

    /**
    * Called when user presses arrow up key while focus is in search field or the
    * autocomplete dropdown. To focus the previous result in autocomplete results
    * or to jump back up to the search field
    *
    * @param {object} ev - the event
    */
    focusPrevious(ev) {
        const sibling = ev.target.parentElement.previousSibling;
        if (sibling) {
            sibling.querySelector('.find-store-automplete-results__button').focus();
        } else {
            this.elements.searchInput.focus();
        }
        ev.preventDefault();
    }

    /**
    * Called when user presses arrow down key while focus is in search field or the
    * autocomplete dropdown. To focus the next result in autocomplete results
    * or to jump back up to the search field
    *
    * @param {object} ev - the event
    */
    focusNext(ev) {
        const sibling = ev.target.parentElement.nextSibling;
        if (sibling) {
            sibling.querySelector('.find-store-automplete-results__button').focus();
        } else {
            this.elements.searchInput.focus();
        }
        ev.preventDefault();
    }


    /**
    * Called when arrow up or arrow down keys are pressed while in search input
    * to focus the autocomplete dropdown
    *
    * @param {object} ev - the event
    * @param {boolean} focusFirstItem - should focus be on first item? (if arrow down is pressed - otherwise it will be on the last)
    */
    focusAutocomplete(ev,focusFirstItem) {
        const items = this.elements.autoCompleteResults.querySelectorAll('.find-store-automplete-results__button');
        if (items && items.length) {
            if(focusFirstItem) {
                items[0].focus();
            } else {
                items[items.length-1].focus();
            }
        }
        ev.preventDefault();
    }

    /**
    * Catch events for: input, arrow keys up or down, focusout from the input field
    */
    initSearchInput() {
        this.elements.searchInput.addEventListener('input', (e) => {this.debounceListener(e)});
        this.elements.searchInput.addEventListener('keyup', (e) =>
            {
                if(e.keyCode === 40) {//arrow down
                    this.focusAutocomplete(e,true);
                }

                if(e.keyCode === 38) {//arrow up
                    this.focusAutocomplete(e,false);
                }
            }
        );
        this.elements.searchInput.addEventListener('focusout', () => {this.checkCloseSuggestionBox()});
    }

    /**
    * Catch events for arrow keys up or down from the autocomplete dropdown
    */
    initAutoCompleteItems() {
        this.elements.searchAutoCompleteItems.addEventListener('keyup', (e) => {
            if(e.keyCode === 38) {//arrow up
                this.focusPrevious(e);
            }
            if(e.keyCode === 40) {//arrow down
                this.focusNext(e);
            }
        });
    }

    /**
    * Add scripts for Google Maps to the DOM
    */
    addMapsScript() {
        const googleMapsLoadingScript = document.createElement('script');
        googleMapsLoadingScript.type = 'text/javascript';
        googleMapsLoadingScript.src = `https://maps.googleapis.com/maps/api/js?key=${this._mapsApiKey}&libraries=places&callback=initMap`;
        googleMapsLoadingScript.async = false;
        document.getElementsByTagName('head')[0].appendChild(googleMapsLoadingScript);
    }

    /**
    * Remove the list of suggestions when the user
    * clicks outside of the searh or suggestion box.
    */
    checkCloseSuggestionBox() {
        setTimeout(() => {
            const { activeElement } = document;
            if (activeElement.closest('inventory-search-form, inventory-search-form-locator')) {
                return;
            }
            this.suggestions = [];
            this.elements.autoCompleteResults._render(this.suggestions);
        });
    }

    /**
    * Debounced search from search input field
    */
    debounceListener(e) {
        if (e.target.value === '') {
            this.elements.searchInput.value = '';
            this.firstResult = [];
        }

        if (this.timeoutRef !== null) {
            clearTimeout(this.timeoutRef);
        }

        if (e.target.value !== '') {
            this.elements.searchInput.value = e.target.value;
            this.timeoutRef = setTimeout(() => {
                this.searchLocation(e.target.value)
                .then((results) => {
                    this.suggestions = results;
                    this.elements.autoCompleteResults._render(this.suggestions);
                    this.firstResult = results[0];
                });
            }, 800);
        }
    }

    /**
    * Calls the google places autocomplete api to get location
    * suggestions for the passed query string.
    * Returns an array of possible locations from the api
    * wrapped in a promise.
    *
    * @param {string} query - name of location to search for
    */
    searchLocation(query) {
        return new Promise(((resolve, reject) => {
            this._autocompleteService.getPlacePredictions({
                input: query,
                componentRestrictions: this.options.componentRestrictions,
                types:['postal_code','locality','sublocality','administrative_area_level_1','administrative_area_level_2']
            }, (place, status) => {
                if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
                    reject(new Error(status));
                } else {
                    resolve(place);
                }
            });
        }));
    }

    /**
    * Update the results to show on the element store-inventory-results
    * and activate rendering of them
    */
    updateLocationsToShow(onLoadStoreLocator) {
        this.$storeInventoryResults.results = this.markerLocations;
        if(onLoadStoreLocator) {
            this.$storeInventoryResults.results = this.$storeInventoryResults.allStores;
        }
        this.$storeInventoryResults._render();
        // empty the array
        this.locationsToShow=[];
    }

    /**
    * Setup marker for the map (no map used in Find in store)
    *
    * @param {object} location - the location
    * @param {object} iconData - info on what the icon of the marker should look like
    * @param {object} markerInfoParam - info on what the marker should look like
    * @param {object} iconNumber - icon number - for controlling z-index
    */
    setupMarker(location, iconData, markerInfoParam, iconNumber) {
        const markerInfo = markerInfoParam;
        const newMarker = this.setMarker(location,true);
        // add event listener to open a element that shows more details about the location
        const clickEvents = {
            'showInfoWindow': () => this.openDetailsView.bind(this, location,newMarker)
        };

        newMarker.addListener('click', clickEvents[this.mapConfig.Markers.MarkerClickAction]());
        markerInfo.marker = newMarker;
        markerInfo.visible = true;
        newMarker.setZIndex(20000 - iconNumber);
        markerInfo.hoverIcon = iconData;
        markerInfo.normalIcon = iconData;

        return newMarker;
    }

    /**
    * Set the marker on the map (no map used in Find in store)
    *
    * @param {object} location - the location
    * @param {boolean} icon - is there an icon?
    */
    setMarker(location, icon = true) {
        if ('lat' in location && 'lng' in location) {
            const marker = new window.google.maps.Marker({
                position: {
                    lat: parseFloat(location.Latitude),
                    lng: parseFloat(location.Longitude)
                },
                map: this.map,
                zIndex: 20000
            });
            if(icon) {
                marker.setIcon(
                    this.mapConfig.icons.normal
                );
            }
            return marker;
        }
        throw new Error('setMarker: no lat or lng attributes');
    }

    /**
    * Manipulates the detailViewData object so the details view
    * is shown for a specific location.
    * The location is bound as this object to the function (I had
    * issues adding it as dynamic parameter in the event handler).
    *
    * openDetailsView called -> change detailViewData value -> show location-detail component
    */
    openDetailsView(location,marker) {
        const infowindowContent = this.infoWindowContentString.replace('###',location.StoreName);

        //currentMarker is the last active marker (the last to open a infowindow)
        //we are about to open a new infowindow, so set icon back to normal
        if(this.currentMarker) {
            this.currentMarker.setIcon(this.mapConfig.icons.normal);
        }

        //we are about to open a new infowindow, so close the old one
        this.infowindow.close();

        // set up this.infowindow
        this.infowindow = new window.google.maps.InfoWindow({
            content: infowindowContent,
            ariaLabel: location.StoreName
          });

        // open this.infowindow
        this.infowindow.open({
            anchor: marker,
            map: this.map
        });

        // change marker icon for clicked marker
        this.currentMarker = marker;
        marker.setIcon(
            this.mapConfig.icons.active
        );

        // calculate position of infowindow and place it
        const storeLocator = this.closest('store-locator');
        if(!storeLocator) {return;}

        const resultListItemToOpen = storeLocator.querySelector(`#store-inventory-results-${location.StoreNumber}`);
        if(!resultListItemToOpen) {return;}

        resultListItemToOpen.querySelector('.store-inventory-result__locator-more-info').open = true;

        const scrollContainer = storeLocator.querySelector('store-inventory-results-locator'); // Replace 'container' with the ID of your div
        scrollContainer.scrollTop = resultListItemToOpen.offsetTop;

        this.detailViewData.location = location;
    }

    /**
    * When a location has been selected, update map results (no map used in Find in store)
    */
    locationsChanged(onLoadStoreLocator) {
        this.markerLocations = this.sortedLocationsWithDistanceToUserSearch;

        if(this.clusterMarkers) {
            this.clusterMarkers.clearMarkers()
        }

        const locations = this.$storeInventoryResults.allStores;

        /**
        * Clean up old markers in case we fetched new location data after a filter operation.
        */
        if ( this.updateAfterFetchingNewLocations ) {
            Object.entries(this.markers).forEach(marker => {
                if (marker[1] && marker[1].marker) { marker[1].marker.setMap(null); }
            });
            this.markers = {};
            this.updateAfterFetchingNewLocations = false;
        }

        this.updateLocationsToShow(onLoadStoreLocator);

        let tempMarkers = [];
        // create new markers

        const pinSize = new window.google.maps.Size(this.mapConfig.Markers.MarkerSize, this.mapConfig.Markers.MarkerSize);

        const iconData = {
            scaledSize: pinSize,
            size: pinSize
        };
        locations.forEach((location, index) => {
            let markerInfo = {
                storeNumber: null,
                number: -1,
                marker: null,
                normalIcon: null,
                hoverIcon: null,
                currentState: false,
                visible: false
            };


            const iconNumber = index + 1;
            markerInfo.storeNumber = location.StoreNumber;
            if (location.StoreNumber in this.markers) {
                // update existing markers
                const {
                    visible, number //,markerData
                } = this.markers[location.StoreNumber];
                let marker = null;
                if(this.map) {
                    marker = this.setupMarker(location, iconData, markerInfo, iconNumber);
                }
                if (marker) {

                    // check if icon should be updated (has number changed)
                    if (number !== -1 && number !== iconNumber) {
                        iconGenerator.generateNumberPinIcons(iconNumber).then(([normalIcon, hoverIcon]) => {
                            const markerInfo = this.markers[location.StoreNumber];
                            markerInfo.normalIcon = normalIcon;
                            markerInfo.hoverIcon = hoverIcon;
                            markerInfo.number = iconNumber;
                            // update hover state once icons are regenerated
                            this.updateHoverForMarker(markerInfo, location, true);
                        });
                    } else {
                        // update hover state
                        this.updateHoverForMarker(this.markers[location.StoreNumber], location);
                    }
                    // show hidden markers
                    if (!visible) {
                        marker.setMap(this.map);
                        this.markers[location.StoreNumber].visible = true;
                    }
                    // update shown markers based on selected max distance
                    if (location.distanceToUserLocation > this.selectedDistance) {
                        marker.setMap(null);
                        this.markers[location.StoreNumber].visible = false;
                    }
                    tempMarkers.push(marker)
                }
            } else {
                this.markers[location.StoreNumber] = markerInfo;

                let tempMarker = this.setupMarker(location, iconData, markerInfo, iconNumber);
                tempMarkers.push(tempMarker)
            }
        });

        //(no map used in Find in store, so outcommented this to avoid errors)
        if(this.isStoreLocator){
            const mmap = this.map;
            // this.clusterMarkers = new markerClusterer.MarkerClusterer({ mmap, tempMarkers });
            this.clusterMarkers = new MarkerClusterer({ mmap, tempMarkers });
        }
        // this.clusterMarkers = new MarkerClusterer(map, tempMarkers, config.clusterMarker, {
        //     styles: config.clusterMarker
        // });
        // this.clusterMarkers.setGridSize(20);
    }

    /**
    * Update hover state on marker
    */
    updateHoverForMarker(markerInfo, location, forceUpdate = false) {
        const {
            marker, currentState, number//normalIcon, hoverIcon,
        } = this.markers[location.StoreNumber];
        if (forceUpdate || currentState !== location.LocationSelected) {
            markerInfo.currentState = location.LocationSelected;
            // marker.setIcon(location.LocationSelected ? hoverIcon : normalIcon);
            marker.setZIndex(location.LocationSelected ? 20005 : 20000 - number);
        }
    }

    /**
    * This function calculates the distance between two points (given the
    * longitude/latitude of those points) for a given unit.
    * @param {float} lat1 - Latitude of point 1 (in decimal degrees)
    * @param {float} lon1 - Longitude of point 1 (in decimal degrees)
    * @param {float} lat2 - Latitude of point 2 (in decimal degrees)
    * @param {float} lon2 - Longitude of point 2 (in decimal degrees)
    */
    distance(lat1, lon1, lat2, lon2) {
        if ((lat1 === lat2) && (lon1 === lon2)) {
            return 0;
        }

        const radlat1 = Math.PI * lat1 / 180;
        const radlat2 = Math.PI * lat2 / 180;
        const theta = lon1 - lon2;
        const radtheta = Math.PI * theta / 180;
        let dist = Math.sin(radlat1) * Math.sin(radlat2)
        + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
        if (dist > 1) {
            dist = 1;
        }
        dist = Math.acos(dist);
        dist = dist * 180 / Math.PI;
        dist = dist * 60 * 1.1515;

        return dist;
    }

    /**
    * Calculates all client locations (stores) in the passed radius to
    * the passed user location and adds a property indicating the distance to the
    * current user location. Afterwards it updates the 'sortedLocationsWithDistanceToUserSearch'
    * array with those locations.
    *
    * @param {object} userLocation - the place_id of the location selected by the user
    * @param {boolean} triggerMapCenter - indicates if the map should be centered around the
    * passed location
    */
    findLocationsInRadius(userLocation, triggerMapCenter = true) {
        // add query param to url after user selects a suggestion
        // setQueryParamsInUrl(userLocation);//TODO maybe for store locator
        this._placesService.getDetails({
            fields: ['name', 'geometry'],
            placeId: userLocation.place_id
        }, (place, status) => {
            userLocation.lat = place.geometry.location.lat();
            userLocation.lng = place.geometry.location.lng();
            if (triggerMapCenter) {
                // set current user location with long, lat
                this.currentUserLocation = userLocation;
            }
            // save the current map center location
            this.currentMapCenterLocation = userLocation;

            // empty the array
            this.sortedLocationsWithDistanceToUserSearch = [];

            if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
                throw new Error(status);
            } else {
                this.dealers = this.$storeInventoryResults.allStores;
                this.dealers.forEach((location) => {
                    // enrich location object with a property indicating
                    // the distance to the user and long, lat values
                    location.distanceToUserLocation = this.distance.apply(null, [
                        place.geometry.location.lat(),
                        place.geometry.location.lng(),
                        location.Latitude,
                        location.Longitude
                    ]);
                    this.sortedLocationsWithDistanceToUserSearch.push(location);
                    this.markerLocations.push(location);
                });
                const sortFn = (a, b) => a.distanceToUserLocation - b.distanceToUserLocation;
                this.sortedLocationsWithDistanceToUserSearch.sort(sortFn);
                this.locationsChanged();
            }
        });
    }


    /**
    * Init map services needed
    */
    initMapsFromFile() {
        //tbb
        this._autocompleteService = new window.google.maps.places.AutocompleteService();
        this._placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
    }
}
customElements.define('inventory-search-form', InventorySearchForm);

class StoreInventoryResults extends HTMLElement {
    /**
    * Template for showing stock level in the chosen store before opening overlay
    *
    * @param {object} store - the chosen store
    */
    static linkTextTemplate(store) {
        const shownStockText = findInStoreTranslations.stockLevel[store.StockLevel] ? findInStoreTranslations.stockLevel[store.StockLevel] : `<i>${findInStoreTranslations.unknowStockStatus}</i>`;
        return `
        <span>${store.StoreName}: </span><span class="find-in-store__button-stock-level find-in-store__button-stock-level--${store.StockLevel}">${shownStockText}</span>
        <span class="link">${findInStoreTranslations.findInOtherStores}</span>
        `;
    }

    static getOpeningHoursData(item, dateData, pageLanguage) {
        let objSpecialOpeningHoursComingMonths2 = [];
        item.SpecialOpeningHours.forEach(specialOpeningElement => {
            let arrStartDate = specialOpeningElement.StartDate.split('/');
            arrStartDate = arrStartDate.map(str => parseInt(str));
            const [startDateNumber, startMonth, startYear] = arrStartDate;
            const itemStartDate = new Date(startYear, startMonth - 1, startDateNumber);

            let arrEndDate = specialOpeningElement.EndDate.split('/');
            arrEndDate = arrEndDate.map(str => parseInt(str));
            const [endDateNumber, endMonth, endYear] = arrEndDate;
            const itemEndDate = new Date(endYear, endMonth - 1, endDateNumber);

            if(endYear < dateData.currentYear) { return; }
            if(startYear > dateData.nextMonthYear) { return; }
            if(startYear === dateData.nextMonthYear && startMonth > dateData.nextMonth ) { return; }
            if(endYear === dateData.currentYear && endMonth < dateData.currentMonth){ return; }
            if(endYear === dateData.currentYear && endMonth === dateData.currentMonth && endDateNumber < dateData.currentDateNumber){ return; }
            if(specialOpeningElement.StartDate === specialOpeningElement.EndDate) {
                objSpecialOpeningHoursComingMonths2[specialOpeningElement.StartDate] = specialOpeningElement;
            }else {
                let currentStartDate = itemStartDate;
                let dateCounter = 1;
                while(currentStartDate <= itemEndDate) {
                    const currentStartDateString = `${currentStartDate.getDate()}/${currentStartDate.getMonth() + 1}/${currentStartDate.getFullYear}`;
                    currentStartDate =  new Date(currentStartDate.getTime() + dateCounter * 24 * 60 * 60 * 1000);
                    dateCounter++;
                    objSpecialOpeningHoursComingMonths2[currentStartDateString] = specialOpeningElement;
                }
            }
        });

        let arrOpeningHoursThisWeek = [];
        let arrSpecialOpeningHoursComingMonths = [];

        const timeDifference  = dateData.lastDayOfNextMonth - dateData.currentDate;
        const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
        for (let m = 0; m <= daysDifference; m++) {
            const loopDate = new Date(dateData.currentDate.getTime() + m * 24 * 60 * 60 * 1000);
            const loopDateDate = loopDate.getDate();
            const loopDateMonth = loopDate.getMonth() + 1;
            const loopDateDateWith2Chars = loopDateDate<10 ? `0${loopDateDate}`:loopDateDate;
            const loopDateMonthWith2Chars = loopDateMonth<10 ? `0${loopDateMonth}`:loopDateMonth;
            const strDate = `${loopDateDateWith2Chars}/${loopDateMonthWith2Chars}/${loopDate.getFullYear()}`
            const weekDayNumber = loopDate.getDay();
            if(m < 7){
                let thisWeekObj = {
                    weekDayName: dateData.weekDaysArray[weekDayNumber],
                    CloseHoursAsInt: null,
                    CloseMinutesAsInt: null,
                    OpenHoursAsInt: null,
                    OpenMinutesAsInt: null,
                    openingHoursString: findInStoreTranslations.openingStatus.closed,
                    hasSpecialOpeningHours: false,
                    storeIsClosedAllDay: true
                };

                if(objSpecialOpeningHoursComingMonths2[strDate]){
                    thisWeekObj.hasSpecialOpeningHours = true;
                    if(!objSpecialOpeningHoursComingMonths2[strDate].StoreIsClosed) {
                        thisWeekObj.openingHoursString = objSpecialOpeningHoursComingMonths2[strDate].OpeningHours;
                        thisWeekObj.CloseHoursAsInt = objSpecialOpeningHoursComingMonths2[strDate].CloseHoursAsInt;
                        thisWeekObj.CloseMinutesAsInt = objSpecialOpeningHoursComingMonths2[strDate].CloseMinutesAsInt;
                        thisWeekObj.OpenHoursAsInt = objSpecialOpeningHoursComingMonths2[strDate].OpenHoursAsInt;
                        thisWeekObj.OpenMinutesAsInt = objSpecialOpeningHoursComingMonths2[strDate].OpenMinutesAsInt;
                        thisWeekObj.storeIsClosedAllDay = false;
                    }
                }else{
                    const currentStandardOpeningHours = item.OpeningHours.find(x => x.WeekDayAsInt === weekDayNumber);
                    if(currentStandardOpeningHours) {
                        thisWeekObj.openingHoursString = currentStandardOpeningHours.OpeningHours;
                        thisWeekObj.CloseHoursAsInt = currentStandardOpeningHours.CloseHoursAsInt;
                        thisWeekObj.CloseMinutesAsInt = currentStandardOpeningHours.CloseMinutesAsInt;
                        thisWeekObj.OpenHoursAsInt = currentStandardOpeningHours.OpenHoursAsInt;
                        thisWeekObj.OpenMinutesAsInt = currentStandardOpeningHours.OpenMinutesAsInt;
                        thisWeekObj.storeIsClosedAllDay = false;
                    }
                }
                arrOpeningHoursThisWeek.push(thisWeekObj);
            }

            if(objSpecialOpeningHoursComingMonths2[strDate]){
                let objLongTermOpeningHours = {};
                objLongTermOpeningHours.openingHoursString = objSpecialOpeningHoursComingMonths2[strDate].StoreIsClosed ? findInStoreTranslations.openingStatus.closed : objSpecialOpeningHoursComingMonths2[strDate].OpeningHours;
                var options = { year: 'numeric', month: 'long', day: 'numeric' };
                objLongTermOpeningHours.strDate = loopDate.toLocaleDateString(pageLanguage, options);
                arrSpecialOpeningHoursComingMonths.push(objLongTermOpeningHours);
            }
        }
        let objOpeningHourData = {};
        objOpeningHourData.week = arrOpeningHoursThisWeek;
        objOpeningHourData.longTerm = arrSpecialOpeningHoursComingMonths;
        return objOpeningHourData;
    }
    static getOpeningHoursHtml(openingHoursData, isStoreLocator) {
        let openingHours = '';
        openingHoursData.week.forEach(oh => {
            openingHours += `<dt class="store-inventory-result__opening-hours-day">${oh.weekDayName}</dt>`;
            openingHours += `<dd class="store-inventory-result__opening-hours-time">${oh.openingHoursString}</dd>`;
        });


        // special opening hours
        let specialOpeningHours = '';
        openingHoursData.longTerm.forEach(soh => {
            specialOpeningHours += `<dt class="store-inventory-result__opening-hours-day store-inventory-result__opening-hours-day--special">${soh.strDate}</dt>`;
            specialOpeningHours += `<dd class="store-inventory-result__opening-hours-time store-inventory-result__opening-hours-time--special">${soh.openingHoursString}</dd>`;
        });
        if(specialOpeningHours !== '') {
            specialOpeningHours = `<h3 class="h4 store-inventory-result__title-exceptions">${findInStoreTranslations.dateExceptionsHeader}</h3>
            <dl class="store-inventory-result__opening-hours-list store-inventory-result__opening-hours-list--special">${specialOpeningHours}</dl>`;
        }

        const openingHoursDetailsOpen = isStoreLocator ? ' open' : '';
        const summaryText = isStoreLocator ? findInStoreTranslations.openingHoursThisWeek : findInStoreTranslations.moreOpeningHours;
        const extraTitle = isStoreLocator ? '' : `<h2 class="h3 accordion__title">${findInStoreTranslations.openingHoursThisWeek}</h2>`;

        if(openingHours !== '') {
            openingHours = `<div class="accordion accordion--find-in-store"><details class="store-inventory-result__opening-hours"${openingHoursDetailsOpen}>
            <summary class="store-inventory-result__opening-hours-summary">
                <div class="summary__title">
                    <h2 class="h4 accordion__title">${summaryText}</h2>
                </div>
                <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-caret" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path></svg>
            </summary>
                    ${extraTitle}
                    <dl class="store-inventory-result__opening-hours-list">${openingHours}</dl>
            ${specialOpeningHours}
            </details>
            </div>`;
        }
        return openingHours;

    }
    /**
    * Template for showing a store in search results
    *
    * @param {object} item - the store to show
    */
    static resultTemplate(item,resultTemplateParams,isSpecificStoreMode) {
        const idForRadios = item.StoreNumber * 3;
        const isCurrentChecked = resultTemplateParams.preferredStoreFromCookie && resultTemplateParams.preferredStoreFromCookie === idForRadios ? 'checked':'';
        const address2 = item.StoreAddress2 ? `<li class="store-inventory-result__store-address-line store-inventory-result__store-address-line--address">${item.StoreAddress2}</li>` : '';
        const email = item.Email ? `<li class="store-inventory-result__store-address-line"><a href="mailto:${item.Email.replaceAll(' ', '')}">${item.Email.replaceAll(' ', '')}</a></li>` : '';
        const phoneNumber = item.PhoneNumber ? `<li class="store-inventory-result__store-address-line"><a href="tel:${item.PhoneNumber.replaceAll(' ', '')}">${item.PhoneNumber}</a></li>` : '';
        //findInStoreTranslations variable is global and defined on the page
        let mapsStoreLink = item.StorePageLink ? `<div class="store-inventory-result__store-link"><a target="_blank" href="${item.MapsUri}">${findInStoreTranslations.seeStoreOnMap}</a>###</div>` : '';
        const linkCaret = resultTemplateParams.isStoreLocator ? '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-caret" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path></svg>' : '';
        mapsStoreLink = mapsStoreLink.replace('###',linkCaret);

        const openingHoursData = this.getOpeningHoursData(item, resultTemplateParams.dateData, resultTemplateParams.pageLanguage);
        const openingHoursHtml = this.getOpeningHoursHtml(openingHoursData, resultTemplateParams.isStoreLocator);

        const shownStockText = findInStoreTranslations.stockLevel[item.StockLevel] ? findInStoreTranslations.stockLevel[item.StockLevel] : `<i>${findInStoreTranslations.unknowStockStatus}</i>`;
        let stockLevel = '';
        if(!resultTemplateParams.isStoreLocator) {
         stockLevel = `<div class="store-inventory-result__stock-level store-inventory-result__stock-level--${item.StockLevel}">${shownStockText}</div>`;

        }
        const storeName = `<div class="store-inventory-result__store-name">${item.StoreName}</div>`;
        let storeNameLocator = '';
        let storeNameFindInStore = storeName;
        let locatorDetailsOpen = '';
        let locatorSummaryOpen = '';
        let locatorDetailsClose = '';
        let locatorSummaryClose = '';
        let locatorPreferredOpen = '';
        let locatorPreferredClose = '';
        let moreInfoBtn = '';

        const openNow = this.setCurrentlyOpenText(openingHoursData.week, resultTemplateParams.dateData);

        const storeDetailsInitialOpenAttr = isSpecificStoreMode ? 'open' : '';
        if(resultTemplateParams.isStoreLocator) {
            storeNameLocator = storeName;
            storeNameFindInStore = '';
            moreInfoBtn = `<span class="store-inventory-result__locator-more-info-button-button">${findInStoreTranslations.moreInfo}<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-caret" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path></svg></span>`;
            locatorDetailsOpen = `<details class="store-inventory-result__locator-more-info" ${storeDetailsInitialOpenAttr}>`
            locatorSummaryOpen = '<summary class="store-inventory-result__locator-more-info-button">';
            locatorSummaryClose = '</summary>';
            locatorDetailsClose = '</details>';
            locatorPreferredOpen = '<div class="store-inventory-result__locator-preferred-holder">';
            locatorPreferredClose = '</div>';
        }

        return `
        <li class="store-inventory-result" id="store-inventory-results-${item.StoreNumber}" role="listitem">
            ${locatorDetailsOpen}
            ${locatorSummaryOpen}
            ${storeNameLocator}
            ${openNow}
            ${moreInfoBtn}
            ${locatorSummaryClose}
            <div class="store-inventory-result__address">
                ${storeNameFindInStore}
                <ul class="store-inventory-result__store-address-list">
                    <li class="store-inventory-result__store-address-line store-inventory-result__store-address-line--address">${item.StoreAddress1}</li>
                    ${address2}
                    <li class="store-inventory-result__store-address-line store-inventory-result__store-address-line--address">${item.StoreZipCode}  ${item.StoreCity}</li>
                    ${email}
                    ${phoneNumber}
                </ul>
            </div>
            ${stockLevel}
            ${openingHoursHtml}
            ${mapsStoreLink}
            ${locatorDetailsClose}
            <input type="radio" id="set-as-default-${idForRadios}" ${isCurrentChecked} name="set-as-default" value="${idForRadios}" class="store-inventory-result__set-as-default-radio">
            ${locatorPreferredOpen}
            <label class="store-inventory-result__set-as-default-label" for="set-as-default-${idForRadios}">
                <div class="store-inventory-result__set-as-default-text-holder">
                    <div class="store-inventory-result__set-as-default-text store-inventory-result__set-as-default-text--not-chosen">${findInStoreTranslations.setAsMyPreferredStore}</div>
                    <div class="store-inventory-result__set-as-default-text store-inventory-result__set-as-default-text--chosen">${findInStoreTranslations.myPreferredStore}</div>
                </div>
                <div class="store-inventory-result__set-as-default-icon">
                    <svg class="icon icon-checkmark" width="11" height="7" viewBox="0 0 11 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1.5 3.5L2.83333 4.75L4.16667 6L9.5 1" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </div>
            </label>
            ${locatorPreferredClose}
        </li>
        `;
    }

    /**
    * Merge the external arrays of inventory and store info
    */
    mergeStoreAndInventory() {
        let mergedList = this.stores;
        if(this.tagName.toLowerCase() === 'store-inventory-results') {
            mergedList = mergedList.map(x => ({
                ...x,
                ...this.inventory.find(y => y.StoreNumber === x.StoreNumber)
            }));
        }
        mergedList = mergedList.map(z => ({
            ...z,
            coordinates: `${z.Latitude},${z.Longitude}`,
            lat:z.Latitude,
            lng:z.Longitude
        }));
        return mergedList;
    }

    constructor() {
        super();
        this.translations = findInStoreTranslations;
        this.defaultCookieName = 'preferred_store';
        this.pageLanguage =  document.documentElement.getAttribute('lang'); // French (France)
        // Create a formatter with the desired language and options
        const dayFormatter = new Intl.DateTimeFormat(this.pageLanguage, { weekday: 'long' });
        this.weekDaysArray = [];
        // get weekdays in local language once and for all
        const tempDateToGetWeekDays = new Date(2023, 7, 13);//this is known to be a Sunday
        for (let i = 0; i < 7; i++) {
            const loopDate = new Date(tempDateToGetWeekDays.getTime() + i * 24 * 60 * 60 * 1000);
            let localizedDay = dayFormatter.formatToParts(loopDate)[0].value;
            const capitalizeFirstLetter = str => `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
            localizedDay = capitalizeFirstLetter(localizedDay);
            this.weekDaysArray.push(localizedDay);
        }
        this.stores = storeDetails;
        if(!storeDetails) {
            return;
        }
        this.isSpecificStoreMode = this.getAttribute('data-specific-store-mode') == 'true';
        this.inventory = storesInventory;
        this._maxLength = parseInt(this.getAttribute('data-max-list-length'),10);
        this._stockText = {
            outOfStock: this.getAttribute('data-text-outofstock'),
            inStock: this.getAttribute('data-text-instock'),
            fewItemsLeft: this.getAttribute('data-text-fewitemsleft')
        }
        this.allStores = this.mergeStoreAndInventory();
        this.getCurrentDefaultStore();
        this.currentlyChosenStore = null;
        this.initSetAsDefault();
        this.results = [];
        this.hideOutOfStock = false;
        this.hideClass = 'store-inventory-results__list--hide';
        this.resultList = this.querySelector('.store-inventory-results__list');
        this.isStoreLocator = this.tagName.toLowerCase() === 'store-inventory-results-locator';
        if(this.isStoreLocator) {
            this.resultList.classList.add('store-inventory-results__list--store-locator')
        }
    }

    /**
    * Eventlistener for when Set as default is clicked
    */
    initSetAsDefault() {
        this.addEventListener('click', (e) => {
            if(e.target.classList.contains('store-inventory-result__set-as-default-radio')) {
                this.setAsDefault(e.target);
            }
        });
    }

    /**
    * Get current default store and call a function for showing it outside the overlay
    */
    getCurrentDefaultStore() {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${this.defaultCookieName}=`);
        if (parts.length === 2) {
            const cookieValue = parseInt(parts.pop().split(';').shift(),10);
            if(cookieValue) {
                this.preferredStoreFromCookie = cookieValue;
            }
        }

        if(this.preferredStoreFromCookie) {
            this._setTextOnOpenOverlayLink();
        }
    }

    /**
    * If a preferred store is set, change text on the link to
    * open the Find in store-overlay
    */
    _setTextOnOpenOverlayLink() {
        //change text on the link
        const storeNumber = (this.preferredStoreFromCookie / 3) + '';
        const startLink = document.getElementById('find-in-store-start-link');
        const startBtnOuter = document.getElementById('find-in-store__button');
        if(!startLink || !startBtnOuter) {return;}
        if(storeNumber) {
            this.currentlyChosenStore = this.allStores.find(x => x.StoreNumber === storeNumber);
        }
        if(this.currentlyChosenStore) {
            const arr = [];
            arr.push(this.currentlyChosenStore);
            startLink.innerHTML = arr.map(this.constructor.linkTextTemplate).join('');
            //switch classes to show the text
            startBtnOuter.classList.add('find-in-store__button--store-chosen');
        }
    }

    /**
    * Set cookie with preferred store and update info associated to preferred store
    *
    * @param {element} el - the element clicked to set preferred store
    */
    setAsDefault(el) {
        const CookieDate = new Date();
        CookieDate.setFullYear(CookieDate.getFullYear() + 1);
        document.cookie = `${this.defaultCookieName}=${el.value};path=/;expires=${CookieDate.toUTCString()};`;
        this.getCurrentDefaultStore();
    }

    getDateData(testDataAttribute) {
        let obj = {};
        let currentDate = new Date();

        if(testDataAttribute) { // if in test mode and set to a future date
            const pattern = /^[0-9-: ]*$/g; //allow only numbers, dashes, colons and spaces in string
            const testResult = pattern.test(testDataAttribute);
            if(testResult) {
                currentDate = new Date(testDataAttribute);
                const dateAttributeSplit = testDataAttribute.split(' ');
                const timeSplit = dateAttributeSplit[1].split(':');
                currentDate = new Date(dateAttributeSplit[0]);
                currentDate.setHours(timeSplit[0], timeSplit[1], 0, 0);
            }
        }
        obj.currentDateWithTime = new Date(currentDate); // Create a copy with the same time
        obj.currentDate = new Date(currentDate); // Create another copy with the same time

        // Set the time components to midnight
        obj.currentDate.setHours(0, 0, 0, 0);

        obj.currentDateNumber = obj.currentDate.getDate();
        obj.currentMonth = obj.currentDate.getMonth() + 1;
        obj.currentYear = obj.currentDate.getFullYear();
        obj.weekDaysArray = this.weekDaysArray;

        // Calculate the next month and adjust the year if necessary
        obj.nextMonth = obj.currentMonth;
        obj.nextMonthYear = obj.currentYear;

        if (obj.nextMonth >= 12) {
            obj.nextMonth = obj.nextMonth - 12; // January (0-based index)
            obj.nextMonthYear++;
        }
        // Create a new Date object for the last day of the next month
        obj.lastDayOfNextMonth = new Date(obj.nextMonthYear, obj.nextMonth + 1, 0);
        obj.nextMonth = obj.currentMonth + 1;

        return obj;
    }

    /**
    * Show the store search results
    * Filter "out of stock"-stores away if that setting is chosen
    */
    _render() {
        let resultCounter = 0;
        let mapBounds = null;

        if(!this.isStoreLocator) {
            this.resultList.classList.add(this.hideClass);
        }else{
            const InventorySearchForm = document.querySelector('inventory-search-form-locator');
            const searchField = InventorySearchForm.querySelector('#inventory-search-field');
            if(!searchField.value) {
                this.results = this.allStores;
            }

            if(InventorySearchForm.map) {
                const bounds = InventorySearchForm.map.getBounds();
                if(bounds) {
                    const NE = bounds.getNorthEast();
                    const SW = bounds.getSouthWest();
                    mapBounds = {};
                    mapBounds.latMin = SW.lat();
                    mapBounds.latMax = NE.lat();
                    mapBounds.lngMin = SW.lng();
                    mapBounds.lngMax = NE.lng();
                }
            }
        }

        const dateData = this.getDateData(this.getAttribute('data-current-date'));

        const resultTemplateParams = {
            preferredStoreFromCookie: this.preferredStoreFromCookie,
            isStoreLocator: this.isStoreLocator,
            dateData: dateData,
            pageLanguage: this.pageLanguage
        };
        const resultsHTML = this.results
        .filter((item, i) => {
            if(this.hideOutOfStock) {
                if(item.StockLevel !== 'outOfStock' && item.StockLevel !== undefined) {
                    resultCounter++;
                    if(this._maxLength>0) {
                        return resultCounter <= this._maxLength;
                    } else {
                        return true;
                    }
                }
            } else {
                if(!this.isStoreLocator) {
                    if(this._maxLength>0) {
                        return i < this._maxLength;
                    } else {
                        return true;
                    }
                }else{
                    if(mapBounds) {
                        if(item.lat >= mapBounds.latMin && item.lat <= mapBounds.latMax && item.lng >= mapBounds.lngMin && item.lng <= mapBounds.lngMax ) {
                            return true;
                        }else{
                            return false;
                        }
                    }else{
                        return true;
                    }
                }
            }
        })
        .map(x=>this.constructor.resultTemplate(x,resultTemplateParams, this.isSpecificStoreMode))
        .join('');
        if(!this.isStoreLocator || resultsHTML) {
            this.resultList.innerHTML = resultsHTML;
        }else{
            this.resultList.innerHTML = `
            <li class="store-inventory-results__list-empty-result">
            <h4 class="title h2">${findInStoreTranslations.noResultsHeader}</h4>
            <p>${findInStoreTranslations.noResultsText} <a class="store-inventory-results__list-empty-result-link" href="#">${findInStoreTranslations.noResultsButtonText}</a></p></li>`;
        }

        setTimeout(() => {
            this.resultList.classList.remove(this.hideClass)
        })
    }

    static storeOpenState(currentDayHours,currentDayMinutes,openingHoursToday) {
        if(currentDayHours < openingHoursToday.OpenHoursAsInt) {
            //not open yet
            return 'not yet';
        }
        else if(currentDayHours === openingHoursToday.OpenHoursAsInt) {
            if(currentDayMinutes < openingHoursToday.OpenMinutesAsInt) {
                //not open yet
                return 'not yet';
            }
        }

        if(currentDayHours > openingHoursToday.CloseHoursAsInt) {
             //has closed
             return 'closed';
        }else if(currentDayHours === openingHoursToday.CloseHoursAsInt) {
            if(currentDayMinutes >= openingHoursToday.CloseMinutesAsInt) {
                //has closed
                return 'closed';
            }
        }
        return 'open';
    }

    /**
     * set on the results whether the store is open now
     */
    static setCurrentlyOpenText(openingHoursNextWeek, todayDate){
        const todayOpeningHours = openingHoursNextWeek[0];
        let dateNow = todayDate.currentDateWithTime;

        const currentDayHours = dateNow.getHours();
        const currentDayMinutes = dateNow.getMinutes();
        let closedNow = false;
        let openNowText = '';
        if(!todayOpeningHours.storeIsClosedAllDay) {
            const storeOpenStateNow = this.storeOpenState(currentDayHours,currentDayMinutes,todayOpeningHours);
            if(storeOpenStateNow === 'open') {
                openNowText = `${findInStoreTranslations.openingStatus.openNow}. `;

                const oneHour = 60 * 60 * 1000; /* ms */

                const closingTime  = new Date(dateNow.getTime());
                closingTime.setHours(todayOpeningHours.CloseHoursAsInt)
                closingTime.setMinutes(todayOpeningHours.CloseMinutesAsInt)
                const milisecondsLeft = closingTime - dateNow;

                if(milisecondsLeft < oneHour) {
                    const minutesLeft = milisecondsLeft / ( 1000 * 60);
                    openNowText += findInStoreTranslations.openingStatus.closesInXMinutes.replace('yyy',minutesLeft);
                }else{
                    const closingTimeHours = todayOpeningHours.CloseHoursAsInt;
                    const closingTimeMinutes = todayOpeningHours.CloseMinutesAsInt.toString().length < 2 ? '0' + todayOpeningHours.CloseMinutesAsInt : todayOpeningHours.CloseMinutesAsInt;
                    openNowText += `${findInStoreTranslations.openingStatus.closesAt}. `.replace('yyy',`${closingTimeHours}:${closingTimeMinutes}`);
                }
            } else if(storeOpenStateNow === 'closed') {
                openNowText = `${findInStoreTranslations.openingStatus.closedForToday}. `;
                closedNow = true;
            } else if(storeOpenStateNow === 'not yet') {
                const openingTimeHours = todayOpeningHours.OpenHoursAsInt;
                const openingTimeMinutes = todayOpeningHours.OpenMinutesAsInt.toString().length < 2 ? '0' + todayOpeningHours.OpenMinutesAsInt : todayOpeningHours.OpenMinutesAsInt;
                openNowText += `${findInStoreTranslations.openingStatus.opensAt}. `.replace('yyy',`${openingTimeHours}:${openingTimeMinutes}`);
            }

        }else{
            openNowText = `${findInStoreTranslations.openingStatus.closedToday}. `;
            closedNow = true;
        }
        if(closedNow) {
            let nextOpeningDay = null;
            let nextOpeningDayFound = false;
            let daysAhead = 1;
            while(!nextOpeningDayFound && daysAhead < 7) {
                const newDay  = new Date(dateNow.getTime());

                newDay.setDate(dateNow.getDate() + daysAhead);
                const openingHoursNewDay = openingHoursNextWeek[daysAhead];
                if(!openingHoursNewDay.storeIsClosedAllDay) {
                    nextOpeningDayFound = true;
                    nextOpeningDay = openingHoursNewDay;
                }else{
                    daysAhead++;
                }
            }

            if(nextOpeningDayFound) {
                const nextOpeningDayOpeningHours = nextOpeningDay.OpenHoursAsInt;
                const nextOpeningDayOpeningMinutes = nextOpeningDay.OpenMinutesAsInt.toString().length < 2 ? '0' + nextOpeningDay.OpenMinutesAsInt : nextOpeningDay.OpenMinutesAsInt;
                if(daysAhead == 1) {
                    openNowText += `${findInStoreTranslations.openingStatus.opensTomorrowAtX}.`.replace('yyy',`${nextOpeningDayOpeningHours}:${nextOpeningDayOpeningMinutes}`);
                }else{
                    openNowText += `${findInStoreTranslations.openingStatus.opensWeekdayAtX}.`.replace('yyy',nextOpeningDay.weekDayName.toLowerCase()).replace('qqq',`${nextOpeningDayOpeningHours}:${nextOpeningDayOpeningMinutes}`);
                }
            }
        }
        return openNowText;
    }

    /**
    * When component is connected and if you're in testmode, open the Find in store-overlay and the first opening hours
    */
    connectedCallback() {
        //if results should be shown on load
        if(this.isStoreLocator || this.testMode) {
            this.results = this.allStores;
            this.results.sort((a, b) => a.StoreName.localeCompare(b.StoreName)); //sort alpabetically by store name
            this._render();

            if(this.testMode) {
                this.querySelector('.store-inventory-result__opening-hours').setAttribute('open','true')
            }
        }
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue === null || oldValue === newValue) return;
    }

}
customElements.define('store-inventory-results', StoreInventoryResults);


// const mapStyle = null; //TODO: mapStyle must be set dynamically somewhere for later use



class StoreLocator extends FindInStore {
    constructor() {
        super();
    }
}
customElements.define('store-locator', StoreLocator);

class InventorySearchFormLocator extends InventorySearchForm {
    constructor() {
        super();
        this.isSpecificStoreMode = this.getAttribute('data-specific-store-mode') == 'true';
        this.dataMapLatitude = parseFloat(this.getAttribute('data-map-lat'));
        this.dataMapLongitude = parseFloat(this.getAttribute('data-map-lng'));
        this.infoWindowContentString = '<div class="infowindow__storename">###</div>';

        // Get the coordinate values of th map
        this.mapLatitude = this.isSpecificStoreMode && this.dataMapLatitude ? this.dataMapLatitude : 57.02352299285298;
        this.mapLongitude = this.isSpecificStoreMode && this.dataMapLongitude ? this.dataMapLongitude : 13.23080099887064;

        const startZoomLevel = this.isSpecificStoreMode ? 14 : window.innerWidth > 810? 6 : 5;
        this.mapStartSettings={
            zoom: startZoomLevel,
            center: {lat: this.mapLatitude, lng: this.mapLongitude } //TODO: center must be set dynamically somewhere /// DK and Sweden center
        };
    }

    showMarkersInViewport(){
        this.locationsChanged();
    }

    /**
    * Init map services needed
    */
    initMapsFromFile() {
        // Create the map. - incomment this when map needs to be shown in store finder
        this.map = new google.maps.Map(document.getElementById('map'), {
          zoom: this.mapStartSettings.zoom,
          center: this.mapStartSettings.center,
          streetViewControl: false,
          styles: mapStyle //TODO: mapStyle can be set dynamically somewhere for later use
        });

        this.setMarkerIcons();

        //tbb
        this._autocompleteService = new window.google.maps.places.AutocompleteService();
        this._placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
        // google.maps.event.addListenerOnce(this.map, 'idle', ()=> {this.showMarkersInViewport()} );
        google.maps.event.addListener(this.map, 'zoom_changed', ()=> {this.showMarkersInViewport()} );
        google.maps.event.addListener(this.map, 'dragend', ()=> {this.showMarkersInViewport()});

        // Set the origin point when the user selects an address
        const originMarker = new google.maps.Marker({map: this.map});
        originMarker.setVisible(false);

        // infowindow for later use
        this.infowindow = new window.google.maps.InfoWindow({});
        this.currentMarker = null;

        //set markers on load
        this.locationsChanged(true);
    }
}
customElements.define('inventory-search-form-locator', InventorySearchFormLocator);


class StoreInventoryResultsLocator extends StoreInventoryResults {
    constructor() {
        super();
        this.elements = {};
        this.elements.StoreLocator = this.closest('store-locator');
        this.elements.InventorySearchFormLocator = this.elements.StoreLocator.querySelector('inventory-search-form-locator');
        this.elements.InventorySearchFormLocator.init();


        this.addEventListener('toggle',(e)=>{
            if(e.target.classList.contains('store-inventory-result__locator-more-info')) {
                const clickedElement = e.target.closest('.store-inventory-result');
                if(!clickedElement) {return;}
                const clickedStoreNumber = clickedElement.id.replace('store-inventory-results-','');
                const markers = this.elements.InventorySearchFormLocator.markers;
                const correspondingMarker = markers[clickedStoreNumber];

                if(e.target.open) {
                    const commonAncestor = e.target.closest('.store-inventory-results__list');
                    const allDetails = commonAncestor.getElementsByClassName('store-inventory-result__locator-more-info');
                    for (let i = 0; i < allDetails.length; i++) {
                        const element = allDetails[i];
                        if(!element.contains(e.target)) {
                            element.open = false;
                        }
                    }
                    //change marker to active
                    new google.maps.event.trigger( correspondingMarker.marker, 'click' );
                }
            }
        },true);

        this.addEventListener('click',(e)=>{
            if(e.target.classList.contains('store-inventory-results__list-empty-result-link')) {
                e.preventDefault();
                this.elements.InventorySearchFormLocator.resetMap();
                this._render();
            }

        });


    }
}
customElements.define('store-inventory-results-locator', StoreInventoryResultsLocator);

const mapStyle =
[
    {
        'featureType': 'all',
        'elementType': 'geometry.fill',
        'stylers': [
            {
                'weight': '2.00'
            }
        ]
    },
    {
        'featureType': 'all',
        'elementType': 'geometry.stroke',
        'stylers': [
            {
                'color': '#9c9c9c'
            }
        ]
    },
    {
        'featureType': 'all',
        'elementType': 'labels.text',
        'stylers': [
            {
                'visibility': 'on'
            }
        ]
    },
    {
        'featureType': 'landscape',
        'elementType': 'all',
        'stylers': [
            {
                'color': '#f2f2f2'
            }
        ]
    },
    {
        'featureType': 'landscape',
        'elementType': 'geometry.fill',
        'stylers': [
            {
                'color': '#ffffff'
            }
        ]
    },
    {
        'featureType': 'landscape.man_made',
        'elementType': 'geometry.fill',
        'stylers': [
            {
                'color': '#ffffff'
            }
        ]
    },
    {
        'featureType': 'poi',
        'elementType': 'all',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'road',
        'elementType': 'all',
        'stylers': [
            {
                'saturation': -100
            },
            {
                'lightness': 45
            }
        ]
    },
    {
        'featureType': 'road',
        'elementType': 'geometry.fill',
        'stylers': [
            {
                'color': '#eeeeee'
            }
        ]
    },
    {
        'featureType': 'road',
        'elementType': 'labels.text.fill',
        'stylers': [
            {
                'color': '#7b7b7b'
            }
        ]
    },
    {
        'featureType': 'road',
        'elementType': 'labels.text.stroke',
        'stylers': [
            {
                'color': '#ffffff'
            }
        ]
    },
    {
        'featureType': 'road.highway',
        'elementType': 'all',
        'stylers': [
            {
                'visibility': 'simplified'
            }
        ]
    },
    {
        'featureType': 'road.arterial',
        'elementType': 'labels.icon',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'transit',
        'elementType': 'all',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'water',
        'elementType': 'all',
        'stylers': [
            {
                'color': '#46bcec'
            },
            {
                'visibility': 'on'
            }
        ]
    },
    {
        'featureType': 'water',
        'elementType': 'geometry.fill',
        'stylers': [
            {
                'color': '#c8d7d4'
            }
        ]
    },
    {
        'featureType': 'water',
        'elementType': 'labels.text.fill',
        'stylers': [
            {
                'color': '#070707'
            }
        ]
    },
    {
        'featureType': 'water',
        'elementType': 'labels.text.stroke',
        'stylers': [
            {
                'color': '#ffffff'
            }
        ]
    }
]
